/* eslint-disable */
import { getDatabaseType } from "app/components/appwizard/component-wizards/wizards/common/databaseTypes";
import api from "core/api/api";
import { parse as parseTQL } from "core/utils/properties-parser";
import { getParams } from "src/modules/wizards/services/wizards-methods-helper";
import { ConnectionValidationResult } from "./connection-validation-results";

const WIZARD_TYPE_ALIASES: Record<string, string> = {
    SQLSERVER: "MSSQL",
    MSJET: "MSSQL",
    YUGABYTEDB: "YUGABYTE",
    POSTGRESQL: "POSTGRES",
    COSMOSDB: "COSMOS",
    MONGO_COSMOSDB: "MONGOCOSMOS",
    MICROSOFTDATAVERSE: "DATAVERSE",
    S3TABLESICEBERGWRITER: "S3TABLESWRITER",
    FABRICMIRRORWRITER: "FABRICMIRROR"
};

export function normalizeWizardType(type: string): string {
    const raw = (type ?? "").trim();
    if (!raw) return "";
    const resolved = /writer$/i.test(raw) ? raw.toUpperCase() : (getDatabaseType(raw) ?? raw).toUpperCase();
    return WIZARD_TYPE_ALIASES[resolved] ?? resolved;
}

function caseInsensitiveAccessor(properties: Record<string, any>) {
    const index: Record<string, any> = {};
    for (const key of Object.keys(properties)) {
        index[key.toLowerCase()] = properties[key];
    }
    return (name: string) => index[(name ?? "").toLowerCase()];
}

export interface ConnectionMeta {
    kind: "SOURCE" | "TARGET";
    componentName: string;
    adapter: string;
    type: string;
    properties: Record<string, any>;
}

function extractConnectionMetas(tqlContent: string): ConnectionMeta[] {
    const stripped = tqlContent.replace(/\/\*[\s\S]*?\*\//g, "");
    const metaRegex = /CREATE\s+(?:OR\s+REPLACE\s+)?(SOURCE|TARGET)\s+([\w.]+)\s+USING\s+[\w.]+\s*\(/gi;

    const raw: Array<{ kind: "SOURCE" | "TARGET"; componentName: string }> = [];
    let match;
    while ((match = metaRegex.exec(stripped)) !== null) {
        const kind = match[1].toUpperCase() as "SOURCE" | "TARGET";
        const full = match[2];
        const componentName = full.includes(".") ? full.split(".").pop() ?? full : full;
        raw.push({ kind, componentName });
    }

    const parsed = parseTQL(tqlContent);
    return raw.map((m, i) => {
        const { adapter, properties } = parsed[i] ?? { adapter: "", properties: {} };
        const get = caseInsensitiveAccessor(properties);
        const dbProviderType = get("databaseprovidertype");
        const type = normalizeWizardType(dbProviderType ?? adapter);
        return { kind: m.kind, componentName: m.componentName, adapter, type, properties };
    });
}

export function supportedConnectionMetas(tqlContent: string, supportedAdapters: string[]): ConnectionMeta[] {
    const supported = new Set(supportedAdapters.map(a => a.toUpperCase()));
    return extractConnectionMetas(tqlContent).filter(m => supported.has(m.type));
}

const RDBMS_WIZARD_TYPES = new Set([
    "ORACLE",
    "MSSQL",
    "MYSQL",
    "POSTGRES",
    "MARIADB",
    "MARIADBXPAND",
    "OJET",
    "OJETCDC",
    "MSJET",
    "YUGABYTE"
]);

async function buildRdbmsApiParams(type: string, isTarget: boolean, properties: Record<string, any>) {
    const get = caseInsensitiveAccessor(properties);
    const connectionParams: any = {
        username: get("username"),
        password: get("password"),
        connectionUrl: get("connectionurl") ?? get("url"),
        host: get("host"),
        port: get("port"),
        dbName: get("databasename") ?? get("dbname"),
        sid: get("sid"),
        service: get("service"),
        containerName: get("containername"),
        useConnectionProfile: get("useconnectionprofile"),
        connectionProfileName: get("connectionprofilename"),
        encrypted: get("encrypted"),
        useSSL: get("usessl"),
        SSLConfig: get("sslconfig"),
        integratedSecurity: get("integratedsecurity")
    };
    // Non-automated-ILCDC source adapters (e.g. Yugabyte) go through getParams'
    // getJDBCURL() branch instead of building the URL from host/port/etc.
    connectionParams.getJDBCURL = () => connectionParams.connectionUrl;
    return getParams(type, connectionParams, false, isTarget);
}

export async function validateConnections(
    tqlContent: string,
    supportedAdapters: string[]
): Promise<ConnectionValidationResult[]> {
    const metas = supportedConnectionMetas(tqlContent, supportedAdapters);
    const results: ConnectionValidationResult[] = [];

    for (const { kind, componentName, adapter, type, properties } of metas) {
        const apiParams = RDBMS_WIZARD_TYPES.has(type)
            ? await buildRdbmsApiParams(type, kind === "TARGET", properties)
            : { ...properties };

        const response = await (api as any).WizardHelper_ValidateConnection(type, apiParams, null, null, null);
        results.push({
            kind,
            componentName,
            adapter,
            type,
            success: !!response?.result,
            message: response?.message ?? ""
        });
    }

    return results;
}
